"use client";
//import react
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
// External Lib
import { Box } from "@mui/material";
// Internal Lib
import { Label, showToast } from "@int-traderjoes/ui-components-library";
// Contexts
import { useTheme } from "@/context";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { useLibraryComponents } from "@/hooks/useLibraryComponents";

//Custom components
import CustomTooltip from "../Tooltip";
import NewerVersionDocumentsBanner from "@/components/Common/NewerVersionDocumentsBanner";
// services
import AuthService from "@/services/authService";
import FileService from "@/services/fileService";
import PimcoreService from "@/services/pimcoreService";
// Utils
import FormHeader from "@/utils/FormHeader";
import { UpArrow, DownArrow } from "@/assets/images/CommonSvgUtils";
import { convertFileToBase64, RequiredDocumentsValidateFile, getFileCategory } from "@/utils/helper";
import { FILE_VALIDATION_CONFIG } from "@/utils/fileValidationConstants";
import { handleApiError } from "@/hooks/useApiErrorHandler";
//Constants

//Assets
import helpIcon from "@/assets/images/ideation-phase-images/HelpIcon.svg";
// Styles
import "./required-documents.scss";
import { DefaultRequiredDocumentData } from "@/context/data";
import { useFileManagement } from "@/hooks";
import { useGlobalStateContext } from "@/context/GlobalContext";
import ConfirmationBox from "@/components/ConfirmationBox";

export const RequiredDocsHeader: React.FC<{
  isExpanded:boolean; 
  formHeader:string;
  onToggle: () => void;
  theme:string;
}> = ({
  isExpanded,
  formHeader,
  onToggle, 
  theme
}) => {
  return (
    <Box
    className={`form-header cursor-pointer ${isExpanded ? "form-header-expanded" : ""
      }`}
      onClick={onToggle}
  >
    <FormHeader title={formHeader} isExpanded={isExpanded} />
    <Box
      className="pr-4 cursor-pointer"
    >
      {isExpanded
        ? UpArrow("dark")
        : DownArrow(theme)}
    </Box>
  </Box>
  );
};

export const RequiredDocsLabel:React.FC<{
  theme:string;
  label:string;
  isError:boolean;
  required:boolean;
  toolTipMessage:string;
  onToolTipLinkClick?: () => void;
}> =(
  {
    theme,
    label,
    isError,
    required,
    toolTipMessage,
    onToolTipLinkClick,
  }
)=>{
  return (
    <Label
      className={`${theme} label-icon ${isError? "label-error-text": ""} `}
      text={label}
      htmlFor="nda"
      required={required}
    >
      <CustomTooltip tooltipText={toolTipMessage} onLinkClick={onToolTipLinkClick}>
        <Image src={helpIcon} alt="Help Icon" />
      </CustomTooltip>
    </Label>
  );
}

export interface BaseRequiredDocsProps {
  isExpanded: boolean;
  onToggle: () => void;
  isVendor?: boolean;
  vendorKey?: number;
}

interface RequiredDocumentsFormProps extends BaseRequiredDocsProps {  
  formUpdated: () => void;
  isFormDisabled?: boolean;
  vendorId?: string;
  downloadOnly?: boolean;
  /** From API: vendorAssessment.hasExistingVendor */
  hasExistingVendor?: boolean | null;
  /**
   * When set (e.g. guest vendor form), banner also considers getGuestVendor `nda` presence.
   * The banner still requires NDA data in `requiredDocuments` so clearing the field in the UI
   * hides the banner even if the vendor row response has not refreshed yet.
   */
  ndaPresentInGuestVendorApi?: boolean;
}

const RequiredDocumentsForm: React.FC<RequiredDocumentsFormProps> = ({
  isExpanded,
  onToggle,
  isVendor,
  formUpdated,
  isFormDisabled,
  vendorId,
  downloadOnly = false,
  hasExistingVendor: hasExistingVendorFromApi,
  ndaPresentInGuestVendorApi,
}) => {
  const { darkMode } = useTheme();
  const displayMode = () => {
    return darkMode? "dark":"light";
  };
  const darkModeClass = darkMode ? "dark" : "light";

  const { FileUploadDisplay } = useLibraryComponents();

  const [isFileSelected, setIsFileSelected] = useState<boolean>(false);
  const [fileSelected, setFileSelected] = useState<File | null>();
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const { setRequiredDocuments, isVendorAssessmentSubmitClicked, requiredDocuments } = useFormContext();
  const { languageText } = useLanguageContext();
  const requiredDocumentsLabels = languageText?.LABELS?.VENDOR_INFORMATION?.REQUIRED_DOCUMENTS_FORM;
  const requiredDocumentsInfoIconMessages = languageText?.MESSAGES?.INFO_ICON_MESSAGES?.VENDOR_INFORMATION?.REQUIRED_DOCUMENTS_FORM;
  const formHeader = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.REQUIRED_DOCUMENTS;
  const buttonTexts = languageText?.BUTTONS;
  const confirmationBoxMessages = languageText?.MESSAGES?.CONFIRMATION_BOX_MESSAGES;
  const deleteDocumentConfirmTitle =
    confirmationBoxMessages?.CONFIRM_TITLE?.VENDOR_LIBRARY_DOCUMENT_DELETE_CONFIRM_TITLE ||
    "Delete Document";
  const deleteDocumentConfirmMessage =
    confirmationBoxMessages?.CONFIRM_MESSAGE?.VENDOR_LIBRARY_DOCUMENT_DELETE_CONFIRM_MSG ||
    "Are you sure you want to delete?";
  const { setIsLoading } = useGlobalStateContext();
  
  // From API: vendorAssessment.hasExistingVendor (fallback true to preserve previous behavior)
  const isExistingVendor = hasExistingVendorFromApi ?? false;
  const [showBanner, setShowBanner] = useState<boolean>(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  /** True when NDA is present in form state (e.g. auto-populated from assessment API: id/filename, or legacy data/fileId). */
  const hasNdaDataInField = useMemo(() => {
    if (requiredDocuments == null) return false;
    const idOrFileId = requiredDocuments.id ?? requiredDocuments.fileId;
    if (idOrFileId != null && String(idOrFileId).trim() !== "") return true;
    const fn = requiredDocuments.filename;
    if (typeof fn === "string" && fn.trim() !== "") return true;
    const data = requiredDocuments.data;
    if (typeof data === "string" && data.trim() !== "") return true;
    return false;
  }, [requiredDocuments]);

  const ndaBannerAdditionalCondition =
    ndaPresentInGuestVendorApi !== undefined
      ? Boolean(ndaPresentInGuestVendorApi) && hasNdaDataInField
      : hasNdaDataInField;

  const documentFields = useMemo(() => ['ndaDocument'], []);
  
  
  // Top-level constant for validation messages
  const validationMessages = languageText?.MESSAGES?.VALIDATION_MESSAGES;

  const {
    uploadFile,
    downloadFile,
    removeFile,
    getAllFilesForPayload,
    isUploading,
    hasFile,
    getFileName,
    populateExistingFiles,
    clearAllFiles
  } = useFileManagement({
    productId: vendorId || 'unknown',
    variantId: 'nda',
    documentFields,
    onFileStateChange: useCallback((fieldName: string, fileData: any) => {
      console.log(`🔄 [IngredientDetailsForm] NDA file state changed:`, fileData);
      
      // Update ingredient details form with asset ID
      if (fileData && fileData.id) {
        setRequiredDocuments({
            id: fileData.id,
            filename: fileData.filename,
            type: getFileCategory('application/pdf'), // Default type
            data: "", // No base64 data with asset API
        });
        formUpdated();
      }
    }, [setRequiredDocuments, formUpdated]),
          uploadOptions: useMemo(() => ({
            maxSize: 40 * 1024 * 1024, // 40MB
            allowedTypes: ['application/pdf'],
            allowedExtensions: ['pdf'],
            isGuest: isVendor // Use guest API for vendor context
          }), [isVendor]),
    isGuest: isVendor // Use guest API for vendor context
  });

  let requiredDocumentsFormPermissions: any = {};

  // Fetching permissions for fields
  requiredDocumentsFormPermissions = AuthService.fetchPermissions("requiredDocuments");

  // File validation configuration for NDA (PDF only)
  const { allowedTypes: ndaAllowedTypes, toastMessage: ndaToastMessage } = FILE_VALIDATION_CONFIG.NDA_DOCUMENTS;

  const handleFileChange = useCallback(async (file: File | null) => {
    // Respect disabled state
    if (isFieldDisabled("nda") || !requiredDocumentsFormPermissions.nda?.isEditable) {
      return;
    }
    const fieldName = 'ndaDocument'; // Fixed field name for NDA
    
    try {
      if (file === null) {
        // Remove file
        console.log(`🗑️ [IngredientDetailsForm] Removing NDA file`);
        await removeFile(fieldName);
        
        // Clear form data
        setRequiredDocuments({
          filename: "",
          type: "",
          data: "",
          id: ""
        });
        
        // Update UI state
        setFileSelected(null);
        setFileInputKey(prevKey => prevKey + 1);
        formUpdated();
        return;
      }

       // Upload flow
    if (!file) return;
    if (!RequiredDocumentsValidateFile(file, validationMessages)) {
      setFileSelected(null);
      setFileInputKey((prevKey) => prevKey + 1); // Change key to force re-render
      return;
    }
    setIsFileSelected(true);
    setFileSelected(file);


      console.log(`🔄 [IngredientDetailsForm] Uploading NDA file:`, file.name);
      setFileSelected(file);
      
      // Generate custom path for NDA document
      const customPath = `vendor-assessment/${vendorId || 'unknown'}/nda/${file.name}`;
      
      try {
        setIsLoading(true);
        await uploadFile(fieldName, file, customPath);
        console.log(`✅ [IngredientDetailsForm] NDA file uploaded successfully`);
      } finally {
        // Always hide loader, even if upload fails
        setIsLoading(false);
      }
      
    } catch (error) {
      console.error(`❌ [IngredientDetailsForm] Error handling NDA file:`, error);
      setFileSelected(null);
      setFileInputKey(prevKey => prevKey + 1);
      
      // Toast is already shown by useFileManagement hook - no need to show it again
      // Removed duplicate showToast call to prevent double toast notifications
    }
  }, [uploadFile, removeFile, vendorId, setRequiredDocuments, formUpdated, setIsLoading, isFormDisabled, requiredDocumentsFormPermissions]);

  useEffect(() => {
    console.log('🔄 [RequiredDocumentsForm] Required documents:', requiredDocuments);
  }, [requiredDocuments]);

  // Load existing NDA file if asset ID exists
  useEffect(() => {
    const loadExistingNDAFile = async () => {
      try {
        const existingNda = requiredDocuments;
        
        if (existingNda?.id && existingNda?.filename) {
          console.log('📁 [IngredientDetailsForm] Loading existing NDA file:', existingNda);
          
          // Populate the file management with existing asset ID
          const fileMap = {
            ndaDocument: {
              id: existingNda.id,
              filename: existingNda.filename
            }
          };
          
          populateExistingFiles(fileMap);
        }
      } catch (error) {
        console.error('❌ [IngredientDetailsForm] Error loading existing NDA file:', error);
      }
    };

    if (requiredDocuments?.id) {
      loadExistingNDAFile();
    }
  }, [requiredDocuments?.id, requiredDocuments?.filename, populateExistingFiles]);

  const handleDeleteFile = useCallback(async () => {
    await handleFileChange(null);
  }, [handleFileChange]);

  const handleRequestDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleCancelDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleConfirmDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(false);
    void handleDeleteFile();
    showToast({
      type: "success",
      message: validationMessages?.FILE_REMOVED_SUCCESS || "File removed successfully",
    });
  }, [handleDeleteFile, validationMessages?.FILE_REMOVED_SUCCESS]);

  const handleDownloadTemplateOnly = async () => {
    try {
      setIsLoading(true)
      const blob = await PimcoreService.getGuestNDATemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = "Trader-Joe's-Non-Disclosure-Agreement.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      handleApiError(
        error,
        languageText?.MESSAGES?.TOAST_MESSAGES?.FILE_VALIDATION?.NDA_TEMPLATE_DOWNLOAD_ERROR ||
          "Failed to download Non-Disclosure Agreement template."
      );
    }
    finally {
      setIsLoading(false)
    }
  }

  const handleDownloadNda = async () => {
    try {
      const assetId = requiredDocuments?.fileId || requiredDocuments?.id;
      if (assetId) {
        await FileService.downloadFile(assetId, requiredDocuments?.filename || 'nda.pdf', isVendor);
        return;
      }
    } catch (error) {
      handleApiError(
        error,
        languageText?.MESSAGES?.TOAST_MESSAGES?.FILE_VALIDATION?.NDA_FILE_DOWNLOAD_ERROR ||
          "Failed to download NDA file."
      );
    }
  }

  useEffect(() => {
    if (requiredDocuments?.filename) {
      setIsFileSelected(true);
    } else {
      setIsFileSelected(false);
    }
  }, [requiredDocuments])
  
 const isFieldDisabled = (fieldName: string) => {
    if(isFormDisabled) return true
    return false;
  }; 
  const handleDownloadFile = useCallback(async () => {
    const fieldName = 'ndaDocument';
    try {
      await downloadFile(fieldName);
    } catch (error) {
      console.error("Failed to download NDA file:", error);
      handleApiError(
        error,
        validationMessages?.FAILED_DOWNLOAD_ATTACHMENT_ERROR || "Failed to download file."
      );
    }
  }, [downloadFile]);
  
  const fieldName = 'ndaDocument';
  const hasUploadedFile = hasFile(fieldName);
  const fileName = getFileName(fieldName);
  const isUploadingFile = isUploading(fieldName);
  const ndaFieldHasError = !isFileSelected && isVendorAssessmentSubmitClicked;

  return (
    <Box
      className={`form-container ${isExpanded ? "form-container-expanded" : ""
        }`}
    >
      <RequiredDocsHeader
        isExpanded={isExpanded}
        formHeader={formHeader}
        onToggle={onToggle}
        theme={displayMode()}
       />
      <form onSubmit={(e) => e.preventDefault()}>
        
        {isExpanded && (
          <>
            {/* Banner for existing vendors */}
            <NewerVersionDocumentsBanner
              isExistingVendor={isExistingVendor}
              additionalCondition={ndaBannerAdditionalCondition}
              showBanner={showBanner}
              onClose={() => setShowBanner(false)}
              className="nda-banner-container"
              wrapperSx={{ px: '1.5rem' }}
            />
            <Box className="field-group">

              {/* NDA */}
              <Box>
                <RequiredDocsLabel
                theme={darkMode ? "dark" : "light"}
                label={requiredDocumentsLabels?.NDA}
                isError={ndaFieldHasError}
                required={requiredDocumentsFormPermissions.nda.isMandatory}
                toolTipMessage={requiredDocumentsInfoIconMessages?.CP_VA_RDF_UPLOAD_NDA_INFO}
                onToolTipLinkClick={handleDownloadTemplateOnly}
               />
              <FileUploadDisplay
                hasUploadedFile={hasUploadedFile}
                fileName={fileName}
                isUploading={isUploadingFile}
                uploadComplete={!isUploadingFile && (hasUploadedFile || Boolean(requiredDocuments?.id))}
                onFileChange={handleFileChange}
                placeholder={"UPLOAD NON-DISCLOSURE AGREEMENT DOCUMENT"}
                allowedTypes={[...ndaAllowedTypes]}
                toastMessage={ndaToastMessage}
                fileInputValue={fileSelected || null}
                fileInputKey={`nda-file-input-${fileInputKey}`}
                onDownload={handleDownloadFile}
                onDelete={handleRequestDeleteFile}
                darkModeClass={darkModeClass}
                disabled={!requiredDocumentsFormPermissions.nda?.isEditable || isFieldDisabled("nda")}
                hideDownloadButton={!Boolean(requiredDocuments?.id)}
                htmlFor={`nda`}
                required={requiredDocumentsFormPermissions.nda.isMandatory}
                hideDeleteButton={downloadOnly}
                error={ndaFieldHasError}
              />
              {!downloadOnly && (
                <span
                  className="download-file-text"
                  style={{ cursor: 'pointer' }}
                  onClick={handleDownloadTemplateOnly}
                >
                  [{languageText?.BUTTONS?.DOWNLOAD_FILE_TEMPLATE}]
                </span>
              )}
              </Box>
            </Box>
          </>
        )}
      </form>
      <ConfirmationBox
        open={deleteConfirmOpen}
        onClose={handleCancelDeleteFile}
        onDecline={handleCancelDeleteFile}
        onConfirm={handleConfirmDeleteFile}
        title={deleteDocumentConfirmTitle}
        message={deleteDocumentConfirmMessage}
        confirmText={buttonTexts?.DELETE || "Delete"}
        cancelText={buttonTexts?.CANCEL_BUTTON || "Cancel"}
        showCancelButton
      />
    </Box>
  );
};

export default RequiredDocumentsForm;